/* Copyright (c) 2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import styled from "styled-components";
import {
  SVGIcon,
  theme,
  useThemeSwitcher,
  THEME_OPTIONS,
  FlexContainer,
} from "@nordcloud/gnui";
import { useAuth0 } from "~/services/Auth";

const DropItem = styled.li`
  display: flex;
  align-items: center;
  justify-items: center;
  padding: 0.5rem 0.2rem;
  cursor: pointer;
  :hover {
    opacity: 0.7;
  }
  &:last-of-type {
    border-top: 1px solid ${theme.color.border.border01};
  }
`;

const UserName = styled.div`
  font-size: 0.7rem;
  font-weight: bold;
  padding-bottom: 0.2rem;
  border-bottom: 1px solid ${theme.color.border.border01};
  max-width: 100%;
  word-wrap: break-word;
`;

const DropBtn = styled.button`
  flex: 1;
  padding: 0;
  background: none;
  display: flex;
  align-items: center;
  justify-items: center;
  border: none;
  color: ${theme.color.text.text01};
  cursor: pointer;
  font-size: 0.85rem;
  &:hover {
    text-decoration: none;
    color: ${theme.color.text.text01};
  }
`;

const DropList = styled.ul`
  border-radius: 4px;
  padding: 0.5rem;
  margin-bottom: 0;
`;

const IconWrap = styled.div`
  display: flex;
  margin-right: 1rem;
`;

const DropdownWrapStyle = styled.div`
  position: relative;
  cursor: pointer;
  z-index: 100;
`;

const BoxWrap = styled.div`
  min-width: 0;
  padding: 0.55rem 1rem 0.55rem 1rem;
  color: ${theme.color.text.text01};
  background: ${theme.color.background.ui01};
`;

const ShowDropDown = styled.div`
  background: ${theme.color.background.ui01};
  transition: all 0.2s ease-in-out;
  position: absolute;
  opacity: 0;
  visibility: hidden;
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
  top: 100%;
  right: 0rem;
  width: 14rem;
  z-index: 100;

  ${/* sc-selector */ DropdownWrapStyle}:hover & {
    opacity: 1;
    visibility: visible;
  }
`;

const ArrowWrap = styled.div`
  display: block;
  transition: transform 0.2s ease-in-out;
  ${/* sc-selector */ DropdownWrapStyle}:hover & {
    transform: rotate(180deg);
  }
`;

function UserDropdown() {
  const { logout, user } = useAuth0();
  const { currentTheme, setTheme } = useThemeSwitcher();

  const userName = user?.name ?? "";
  const isLight = currentTheme === THEME_OPTIONS.LIGHT;
  const nextTheme = isLight ? THEME_OPTIONS.DARK : THEME_OPTIONS.LIGHT;

  const iconTheme = !isLight ? "sun" : "moon";
  return (
    <DropList>
      <UserName>{userName}</UserName>
      <DropItem>
        <DropBtn onClick={() => setTheme(nextTheme)}>
          <IconWrap>
            <SVGIcon name={iconTheme} />
          </IconWrap>
          Switch to {THEME_OPTIONS[nextTheme].toLowerCase()} mode
        </DropBtn>
      </DropItem>

      <DropItem>
        <DropBtn
          onClick={() => {
            logout({
              returnTo: window.location.origin,
            });
          }}
        >
          <IconWrap>
            <SVGIcon name="logout" />
          </IconWrap>
          Logout
        </DropBtn>
      </DropItem>
    </DropList>
  );
}

type DropdownWrapProps = {
  children?: React.ReactNode;
};

function DropdownWrap({ children }: DropdownWrapProps) {
  return (
    <DropdownWrapStyle>
      <BoxWrap>
        <FlexContainer>
          {children}
          <ArrowWrap>
            <SVGIcon size="sm" name="down" />
          </ArrowWrap>
        </FlexContainer>
        <ShowDropDown>
          <UserDropdown />
        </ShowDropDown>
      </BoxWrap>
    </DropdownWrapStyle>
  );
}

export { DropdownWrap };
